import * as Sentry from "@sentry/react";
import { FunctionComponent, useEffect, useMemo } from 'react';
import { GetAuthedUserPermissionsResponse } from '../../api/dtos';
import AuthenticationActionContext from '../../contexts/AuthenticationActionContext';
import AuthenticationContext from '../../contexts/AuthenticationContext';
import useAuthentication from '../../hooks/useAuthentication';
import { createSentryAnonymousUser } from '../../utilities/errors';
import Error from '../layouts/Error';
import Loading from '../layouts/Loading';
interface IAuthenticationProviderProps {
  initialAuthentication?: GetAuthedUserPermissionsResponse | null;
}
const AuthenticationProvider: FunctionComponent<IAuthenticationProviderProps> = ({
  initialAuthentication,
  children
}) => {
  const {
    data,
    error,
    loading,
    mutate
  } = useAuthentication({
    initialData: initialAuthentication ?? undefined,
    refreshInterval: 300000
  });
  useEffect(() => {
    // no user, clear sentry user.
    if (!data) {
      // set Sentry User back to anonymous.
      Sentry.setUser(createSentryAnonymousUser());
      return;
    }
    // user, set sentry user.
    Sentry.setUser({
      id: data.userName,
      username: data.userName,
      fullName: data.displayName,
      authenticated: true
    });
    return () => {
      // set Sentry User back to anonymous.
      Sentry.setUser(createSentryAnonymousUser());
    };
  }, [data]);
  const actions = useMemo(() => {
    return {
      mutate
    };
  }, [mutate]);
  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error title="Error" description="An error occurred while attempting to retrieve the logged on session." responseStatus={error} />;
  }
  return <AuthenticationActionContext.Provider value={actions} data-sentry-element="AuthenticationActionContext.Provider" data-sentry-component="AuthenticationProvider" data-sentry-source-file="AuthenticationProvider.tsx">
            <AuthenticationContext.Provider value={data ?? null} data-sentry-element="AuthenticationContext.Provider" data-sentry-source-file="AuthenticationProvider.tsx">
                {children}
            </AuthenticationContext.Provider>
        </AuthenticationActionContext.Provider>;
};
export default AuthenticationProvider;